import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Loader,
  ScrollArea,
  useDebounceCallback,
} from "@devhop/ui";
import { Check, Search } from "lucide-react";
import { useState } from "react";
import { useLatestShift, useOrders } from "../api/queries";

type OrderSelectorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string | null;
  excludeOrderId: string;
  onSelect: (orderId: string) => void;
};

export function OrderSelectorDialog({
  open,
  onOpenChange,
  branchId,
  excludeOrderId,
  onSelect,
}: OrderSelectorDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounceCallback(
    (value: string) => setSearchQuery(value),
    300,
  );

  // Get latest shift for current shift filter
  const { data: latestShift } = useLatestShift(branchId);

  const { data: ordersData, isLoading } = useOrders(
    branchId,
    {
      status: ["OPEN"],
      search: searchQuery || undefined,
      shiftId: latestShift?.id,
    },
    { limit: 50, offset: 0 },
    { field: "createdAt", order: "desc" },
  );

  const orders =
    ordersData?.data.filter((order) => order.id !== excludeOrderId) ?? [];

  const handleSelect = (orderId: string) => {
    onSelect(orderId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <ScrollArea className="max-h-[400px]">
          <DialogHeader>
            <DialogTitle>ເລືອກ Order ເປົ້າຫມາຍ</DialogTitle>
            <DialogDescription>ເລືອກ order ທີ່ຕ້ອງການຍ້າຍລາຍການໄປ</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="ຄົ້ນຫາ Order..."
                className="pl-9"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>

            {/* Orders list */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader />
              </div>
            ) : orders.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Search className="h-8 w-8 text-muted-foreground" />
                  </EmptyMedia>
                  <EmptyTitle>ບໍ່ພົບ Order</EmptyTitle>
                  <EmptyDescription>
                    ບໍ່ມີ order ທີ່ສາມາດຍ້າຍລາຍການໄປໄດ້
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => handleSelect(order.id)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent",
                    )}
                  >
                    <div className="flex-1">
                      <p className="font-medium">Order #{order.orderNo}</p>
                      <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                        {order.tableName && <span>ໂຕະ: {order.tableName}</span>}
                        <span>•</span>
                        <span>
                          {Number(order.grandTotal).toLocaleString("lo-LA")} ₭
                        </span>
                      </div>
                    </div>
                    <Check className="h-4 w-4 text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              ຍົກເລີກ
            </Button>
          </DialogFooter>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
